import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * "Home page = the last page this browser visited."
 *
 * Every navigation to a known page is saved to localStorage. When the app is
 * opened fresh at "/" (typing the site address, a bookmark of the root, the
 * "Go to fpm" button...) the user is sent to that saved page instead. A
 * browser that has never visited gets the normal home page: the Charging
 * dashboard.
 *
 * Only the initial page load is redirected. Clicking Dashboard -> Charging
 * inside the app still goes to "/" as usual. Bookmarks and links to a specific
 * page (e.g. /chargers) are never overridden.
 */

const STORAGE_KEY = 'odp-fpm-last-page';

/** Pages worth returning to. Keep in sync with the <Routes> in App.tsx. */
const REMEMBERABLE_PATHS = new Set([
  '/',
  '/dashboard/energy',
  '/setup',
  '/power',
  '/chargers',
  '/plugs/commission',
  '/plugs/edit',
  '/wifi-switch',
  '/nuc',
  '/fleet',
  '/events',
]);

function readLastPage(): string | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && REMEMBERABLE_PATHS.has(saved) ? saved : null;
  } catch {
    return null; // storage blocked (private mode, etc.) -> behave as first visit
  }
}

function writeLastPage(path: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, path);
  } catch {
    /* ignore: remembering the page is best-effort */
  }
}

// Evaluated once per page load, before this session saves anything, so it is
// the page the user was on when they last left.
const RESTORE_TARGET: string | null =
  typeof window !== 'undefined' && window.location.pathname === '/' ? readLastPage() : null;

// Flips true once the app has mounted, so later visits to "/" are left alone.
let initialLoadHandled = false;

/** Element for the "/" route: the home page, or a one-time jump to the last page. */
export function HomeRoute({ children }: { children: React.ReactNode }) {
  if (RESTORE_TARGET && RESTORE_TARGET !== '/' && !initialLoadHandled) {
    return <Navigate to={RESTORE_TARGET} replace />;
  }
  return <>{children}</>;
}

/** Saves the current page on every navigation. Renders nothing. */
export function RememberLastPage() {
  const { pathname } = useLocation();

  useEffect(() => {
    const restoring = !initialLoadHandled && RESTORE_TARGET && RESTORE_TARGET !== '/' && pathname === '/';
    initialLoadHandled = true;
    // While bouncing from "/" to the saved page, don't overwrite the saved page with "/".
    if (restoring) return;
    if (REMEMBERABLE_PATHS.has(pathname)) writeLastPage(pathname);
  }, [pathname]);

  return null;
}
